import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  TrendingUp, Plus, Trash2, Info, ExternalLink,
  Zap, Wallet, Megaphone, Lock,
} from 'lucide-react';
import { toast } from 'sonner';

const PLATFORMS = ['YouTube', 'TikTok', 'Instagram', 'Facebook'];
const FUTURE_INTEGRATIONS = [
  { key: 'easymoney_sync', label: 'EasyMoney Automatic Income Sync', icon: Wallet, blurb: 'Actual income would sync here automatically once connected.' },
  { key: 'easymoney_ads', label: 'EasyMoney Ads Bucket', icon: Zap, blurb: 'Available ad budget would be shown here once connected.' },
  { key: 'ads_engine', label: 'Consistency Ads Engine', icon: Megaphone, blurb: 'High-performing content from your Ads folder could become ad creatives here, subject to your controls and budget.' },
];

function monthKey(dateStr) { return (dateStr || '').slice(0, 7); }
function monthLabel(key) { const [y, m] = key.split('-'); return new Date(Number(y), Number(m) - 1).toLocaleDateString(undefined, { month: 'short', year: '2-digit' }); }

/** Revenue Intelligence detail dialog. "Actual income" below is computed
 * from the real manual_income_entries already stored via Creator Profile
 * (read-only reuse — no second income store). Anything added via "Add
 * Entry" here stays in local, clearly-labeled demo state only; it is never
 * written back, so real entries always stay the source of truth. */
export default function RevenueIntelligenceDialog({ open, onOpenChange, creatorInfo }) {
  const navigate = useNavigate();
  const realEntries = creatorInfo?.manual_income_entries || [];
  const [demoEntries, setDemoEntries] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState({ platform: 'YouTube', amount: '', date: new Date().toISOString().split('T')[0], notes: '' });

  const allEntries = useMemo(() => [...realEntries, ...demoEntries], [realEntries, demoEntries]);
  const actualTotal = allEntries.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  const byPlatform = useMemo(() => {
    const m = {};
    PLATFORMS.forEach(p => { m[p] = 0; });
    allEntries.forEach(e => { m[e.platform] = (m[e.platform] || 0) + (parseFloat(e.amount) || 0); });
    return m;
  }, [allEntries]);
  const byMonth = useMemo(() => {
    const m = {};
    allEntries.forEach(e => { const k = monthKey(e.date); if (k) m[k] = (m[k] || 0) + (parseFloat(e.amount) || 0); });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b)).slice(-6);
  }, [allEntries]);

  const addDemoEntry = () => {
    if (!draft.amount || parseFloat(draft.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setDemoEntries(prev => [...prev, { ...draft, amount: parseFloat(draft.amount), id: `demo-${Date.now()}`, _demo: true }]);
    setDraft({ platform: 'YouTube', amount: '', date: new Date().toISOString().split('T')[0], notes: '' });
    setShowAdd(false);
    toast.success('Added to this preview (demo) — not saved. Add real entries in Creator Profile.');
  };
  const removeDemoEntry = (id) => setDemoEntries(prev => prev.filter(e => e.id !== id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14171F] border-white/10 sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="revenue-intelligence-dialog">
        <DialogHeader><DialogTitle className="text-white">Revenue Intelligence</DialogTitle></DialogHeader>

        <Alert className="bg-yellow-500/10 border-yellow-500/30">
          <Info className="h-3.5 w-3.5 text-yellow-500" />
          <AlertDescription className="text-[11px] text-yellow-400">
            Estimates are predictive, based on industry averages — not guaranteed income. Content income only for now.
          </AlertDescription>
        </Alert>

        {/* Revenue Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card className="glass border-white/5"><CardContent className="pt-4 pb-4"><p className="text-xs text-[#8A93A6] mb-1">Estimated Monthly Income</p><p className="text-xl font-bold text-[#555E6E]">$-- / mo</p><p className="text-[10px] text-[#555E6E] mt-0.5">Connect platforms to calculate</p></CardContent></Card>
          <Card className="glass border-white/5"><CardContent className="pt-4 pb-4"><p className="text-xs text-[#8A93A6] mb-1">Actual Income (all entries)</p><p className="text-xl font-bold text-white" data-testid="revenue-actual-total">${actualTotal.toFixed(2)}</p><p className="text-[10px] text-[#555E6E] mt-0.5">{demoEntries.length > 0 ? `Includes ${demoEntries.length} demo entry/entries` : 'From Creator Profile'}</p></CardContent></Card>
          <Card className="glass border-white/5"><CardContent className="pt-4 pb-4"><p className="text-xs text-[#8A93A6] mb-1">Variance</p><p className="text-xl font-bold text-[#555E6E]">N/A</p><p className="text-[10px] text-[#555E6E] mt-0.5">Needs a real estimate to compare against</p></CardContent></Card>
        </div>
        <p className="text-[11px] text-[#555E6E]">Previous-period comparison isn't available yet — it needs at least two periods of income history.</p>

        {/* Platform Revenue */}
        <div>
          <Label className="text-[#8A93A6] text-xs uppercase tracking-wider">Platform Revenue</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            {PLATFORMS.map(p => (
              <div key={p} className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5 text-center" data-testid={`revenue-platform-${p.toLowerCase()}`}>
                <p className="text-[11px] text-[#8A93A6]">{p}</p>
                <p className="text-sm font-semibold text-white mt-0.5">${byPlatform[p].toFixed(2)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly trend (real, from actual entries) */}
        <div>
          <Label className="text-[#8A93A6] text-xs uppercase tracking-wider">Monthly Income Trend</Label>
          {byMonth.length === 0 ? (
            <p className="text-xs text-[#555E6E] mt-2">Add income entries across a few months to see a trend here.</p>
          ) : (
            <div className="flex items-end gap-2 mt-2 h-20" data-testid="revenue-monthly-trend">
              {byMonth.map(([k, v]) => {
                const max = Math.max(...byMonth.map(([, vv]) => vv), 1);
                return (
                  <div key={k} className="flex flex-col items-center gap-1 flex-1">
                    <div className="w-full bg-[#3A7BFF]/30 rounded-t" style={{ height: `${Math.max((v / max) * 56, 3)}px` }} title={`$${v.toFixed(2)}`} />
                    <span className="text-[9px] text-[#8A93A6]">{monthLabel(k)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Actual Income Entry */}
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-[#8A93A6] text-xs uppercase tracking-wider">Actual Income Entry</Label>
            <Button size="sm" variant="outline" className="h-7 text-xs border-white/10" onClick={() => setShowAdd(v => !v)} data-testid="revenue-add-entry-toggle"><Plus className="h-3 w-3 mr-1" />Add Entry (demo)</Button>
          </div>
          {showAdd && (
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 mt-2 space-y-2" data-testid="revenue-add-entry-form">
              <div className="grid grid-cols-2 gap-2">
                <Select value={draft.platform} onValueChange={v => setDraft(d => ({ ...d, platform: v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1C1F26] border-white/10">{PLATFORMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" value={draft.amount} onChange={e => setDraft(d => ({ ...d, amount: e.target.value }))} placeholder="Amount" className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="revenue-entry-amount" />
              </div>
              <Input type="date" value={draft.date} onChange={e => setDraft(d => ({ ...d, date: e.target.value }))} className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="revenue-entry-date" />
              <Input value={draft.notes} onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))} placeholder="Notes (optional)" className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="revenue-entry-notes" />
              <Button size="sm" className="bg-[#3A7BFF] hover:bg-[#3A7BFF]/80 h-8 text-xs w-full" onClick={addDemoEntry} data-testid="revenue-entry-save">Save to this preview (demo)</Button>
            </div>
          )}
          <div className="space-y-1.5 mt-2 max-h-40 overflow-y-auto">
            {allEntries.length === 0 && <p className="text-xs text-[#555E6E]">No income entries yet.</p>}
            {allEntries.map((e, i) => (
              <div key={e.id || i} className="flex items-center justify-between text-xs bg-white/[0.02] border border-white/5 rounded-lg px-2.5 py-1.5" data-testid={`revenue-entry-row-${e.id || i}`}>
                <span className="text-white">{e.platform} · ${parseFloat(e.amount).toFixed(2)} · {e.date}{e._demo && <Badge className="ml-1.5 bg-purple-500/15 text-purple-300 border-purple-500/30 text-[9px]">Demo</Badge>}</span>
                {e._demo && <button onClick={() => removeDemoEntry(e.id)} data-testid={`revenue-entry-remove-${e.id}`}><Trash2 className="h-3 w-3 text-[#555E6E] hover:text-red-400" /></button>}
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" className="border-white/10 w-full mt-2 h-8 text-xs" onClick={() => navigate('/app/account/creator-profile')} data-testid="revenue-manage-in-profile-btn">
            <ExternalLink className="h-3 w-3 mr-1.5" />Manage Real Entries in Creator Profile
          </Button>
        </div>

        {/* Estimate Learning */}
        <Alert className="bg-blue-500/10 border-blue-500/30">
          <TrendingUp className="h-3.5 w-3.5 text-blue-400" />
          <AlertDescription className="text-[11px] text-blue-300">
            Estimate accuracy should improve over time as more actual income and platform performance data become available — this is not a live predictive model yet, and no simulated estimate has learned from your real earnings.
          </AlertDescription>
        </Alert>

        {/* Future Integration Readiness */}
        <div>
          <Label className="text-[#8A93A6] text-xs uppercase tracking-wider">Future Integration Readiness</Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
            {FUTURE_INTEGRATIONS.map(f => (
              <div key={f.key} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 opacity-70" data-testid={`future-integration-${f.key}`}>
                <div className="flex items-center gap-1.5 mb-1"><f.icon className="h-3.5 w-3.5 text-[#8A93A6]" /><Lock className="h-2.5 w-2.5 text-[#555E6E]" /></div>
                <p className="text-xs text-white font-medium">{f.label}</p>
                <p className="text-[10px] text-[#555E6E] mt-1">{f.blurb}</p>
                <Badge variant="outline" className="border-white/10 text-[#555E6E] text-[9px] mt-1.5">Not connected yet</Badge>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
